import { Navigate, Outlet, NavLink } from "react-router-dom";
import classNames from "classnames";
import { menu, sectionTitles } from "./menu.map";
import { useConfig } from "../../hooks";
import { isMotiveIntegrationEnabled } from "../../utils/motiveIntegration";
import { isFeatureEnabledFromConfig } from "../../utils/featureFlags";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../constants/access-control";

const settingsFeatureKeyByLabel = {
  Rates: "settings.rates.enabled",
  Dispatch: "settings.dispatch.enabled",
  Templates: "settings.templates.enabled",
  "PDF Templates": "settings.templates.enabled",
  "Task Templates": "settings.taskTemplates.enabled",
  Tags: "settings.tags.enabled",
  "Notification Templates": "settings.notificationTemplates.enabled",
  Users: "settings.users.enabled",
  "Roles Permissions": "settings.users.enabled",
  Billing: "settings.billing.enabled",
  Reports: "settings.reports.enabled",
  "Google Drive": "settings.enabled",
  Advanced: "settings.advanced.enabled",
};

const settingsPermissionByLabel = {
  Users: PERMISSIONS.SETTINGS_USERS_READ,
  "Roles Permissions": PERMISSIONS.SETTINGS_ROLES_READ,
  Billing: PERMISSIONS.SETTINGS_BILLING_READ,
  Tags: PERMISSIONS.SETTINGS_TAGS_MANAGE,
  Templates: PERMISSIONS.SETTINGS_TEMPLATES_MANAGE,
  "PDF Templates": PERMISSIONS.SETTINGS_TEMPLATES_MANAGE,
  "Task Templates": PERMISSIONS.SETTINGS_TEMPLATES_MANAGE,
  "Notification Templates": PERMISSIONS.SETTINGS_TEMPLATES_MANAGE,
  Advanced: PERMISSIONS.SETTINGS_ADVANCED_MANAGE,
  Integrations: PERMISSIONS.INTEGRATIONS_MOTIVE_MANAGE,
  "Google Drive": PERMISSIONS.SETTINGS_ORGANIZATION_MANAGE,
};

const toSettingsPath = (item) =>
  `/settings/${item.toLowerCase().replace(/\s+/g, "-")}`;

const Settings = () => {
  const { appType, configs } = useConfig();
  const { hasAnyPermission, hasPermission } = usePermissions();
  const isFeatureEnabled = (featureKey) =>
    isFeatureEnabledFromConfig(configs, featureKey);

  const hasSettingsAccess = hasAnyPermission([
    PERMISSIONS.SETTINGS_USERS_READ,
    PERMISSIONS.SETTINGS_USERS_MANAGE,
    PERMISSIONS.SETTINGS_ROLES_READ,
    PERMISSIONS.SETTINGS_ROLES_MANAGE,
    PERMISSIONS.SETTINGS_BILLING_READ,
    PERMISSIONS.SETTINGS_BILLING_MANAGE,
    PERMISSIONS.SETTINGS_ORGANIZATION_MANAGE,
  ]);

  const isMenuItemVisible = (item) => {
    const featureKey = settingsFeatureKeyByLabel?.[item];
    const permissionKey = settingsPermissionByLabel?.[item];

    if (item === "Integrations" && !isMotiveIntegrationEnabled(configs)) {
      return false;
    }
    if (featureKey && !isFeatureEnabled(featureKey)) {
      return false;
    }
    if (permissionKey && !hasPermission(permissionKey)) {
      return false;
    }
    if (!permissionKey && item !== "General" && !hasSettingsAccess) {
      return false;
    }
    if (item === "Reports" && appType === "trucking") {
      return false;
    }
    if (item === "Fleet" && appType !== "trucking") {
      return false;
    }
    if (item === "Drivers" && appType !== "trucking") {
      return false;
    }
    if (item === "PDF Templates" && appType !== "trucking") {
      return false;
    }

    return true;
  };

  if (!hasSettingsAccess) {
    return <Navigate to="/" replace />;
  }

  const menuSections = Object.keys(menu)
    .map((sectionKey) => ({
      key: sectionKey,
      title: sectionTitles[sectionKey] || sectionKey,
      items: menu[sectionKey].filter(isMenuItemVisible),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <div className="h-full">
      <div className="grid grid-cols-6 divide-x border-t h-full">
        <div className="p-6 bg-white overflow-y-auto">
          {menuSections.map((section, sectionIndex) => (
            <div
              key={section.key}
              className={classNames("space-y-2", {
                "mt-8": sectionIndex > 0,
              })}
            >
              <div>
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {section.title}
                </span>
              </div>
              <div className="space-y-2 flex flex-col">
                {section.items.map((item) => (
                  <NavLink
                    key={item}
                    to={toSettingsPath(item)}
                    className={({ isActive }) =>
                      classNames(
                        "text-gray-500 cursor-pointer hover:text-gray-600",
                        { "text-primary-700 font-semibold": isActive },
                      )
                    }
                  >
                    {item}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="col-span-5 space-y-4 overflow-scroll p-3">
          <div className="space-y-2 overflow-y-scroll">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;

export const SettingsHeader = (props) => {
  return (
    <div className="flex items-center justify-between">
      <div className="text-gray-800 text-2xl font-bold">{props.title}</div>
      <div>{props.actions}</div>
    </div>
  );
};
